import { flow, isEqual, set } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { ClientDatasetActionTypes, ClientDatasetView, ClientDatasetsState } from './types';
import { HiddenField, HiddenSection } from '../../../datasets/store/types';
import { isHiddenField } from '../../../datasets/store/typeAssertions';

const compareStrings = (a: string, b: string) => a < b ? -1 : 1;
const sortHiddenFields = (hiddenFields: (HiddenField | HiddenSection)[]) => hiddenFields.sort((a, b) => {
    if (a.type == b.type) {
        if (isHiddenField(a) && isHiddenField(b)) {
            return compareStrings(a.fieldId, b.fieldId);
        }
        if (!isHiddenField(a) && !isHiddenField(b)) {
            return compareStrings(a.sectionId, b.sectionId);
        }
    }
    return compareStrings(a.type, b.type);
});

export const INITIAL_STATE: ClientDatasetsState = {
    fetchingAllDatasets: false,
    allDatasets: [],
    datasetView: ClientDatasetView.LIST,
    selectedDataset: null,
    datasetDefinitions: [],
    openFieldsAndSections: [],
    modalDatasetId: null,
    savedHiddenFields: {},
    currentHiddenFields: {},
    isSaving: false,
    hiddenFieldsUpdated: false,
    unsavedChangesModalOpen: false,
    fetchingDatasetDefinitions: false,
    documentHiddenFields: []
};

export const clientDatasetsReducer: Reducer<ClientDatasetsState> = (state = INITIAL_STATE, { type, payload }): ClientDatasetsState => {
    switch (type) {
        case ClientDatasetActionTypes.FETCH_ALL_CLIENT_DATASETS_STARTED:
            return set('fetchingAllDatasets', true, state);
        case ClientDatasetActionTypes.FETCH_ALL_CLIENT_DATASETS_SUCCESSFUL:
            return flow(
                set('allDatasets', payload),
                set('fetchingAllDatasets', false)
            )(state);
        case ClientDatasetActionTypes.FETCH_ALL_CLIENT_DATASETS_FAILED:
            return set('fetchingAllDatasets', false, state);
        case ClientDatasetActionTypes.CLIENT_DATASET_VIEW: {
            const { view, datasetId } = payload;
            return flow(
                set('datasetView', view),
                set('selectedDataset', datasetId),
                set('unsavedChangesModalOpen', false)
            )(state);
        }
        case ClientDatasetActionTypes.FETCH_CLIENT_DATASET_DEFINITIONS_STARTED:
            return flow(
                set('fetchingDatasetDefinitions', true),
                set('datasetDefinitions', []),
                set('savedHiddenFields', {}),
                set('currentHiddenFields', {}),
                set('documentHiddenFields', []),
                set('hiddenFieldsUpdated', false)
            )(state);
        case ClientDatasetActionTypes.FETCH_CLIENT_DATASET_DEFINITIONS_SUCCESSFUL: {
            const { datasetDefinitions, hiddenFields, documentHiddenFields, riskToleranceIsRecalculating } = payload;
            return flow(
                set('datasetDefinitions', datasetDefinitions),
                set('savedHiddenFields', hiddenFields),
                set('currentHiddenFields', hiddenFields),
                set('documentHiddenFields', documentHiddenFields),
                set('hiddenFieldsUpdated', false),
                set('fetchingDatasetDefinitions', false),
                set('riskToleranceIsRecalculating', riskToleranceIsRecalculating)
            )(state);
        }
        case ClientDatasetActionTypes.FETCH_CLIENT_DATASET_DEFINITIONS_FAILED:
            return set('fetchingDatasetDefinitions', false, state);
        case ClientDatasetActionTypes.REMOVE_CLIENT_DATASETS_OPEN_FIELD_SECTION: {
            const openFieldsAndSections = state.openFieldsAndSections.filter(fieldSection => !isEqual(fieldSection, payload));
            return set('openFieldsAndSections', openFieldsAndSections, state);
        }
        case ClientDatasetActionTypes.ADD_CLIENT_DATASETS_OPEN_FIELD_SECTION: {
            const openFieldsAndSections = [...state.openFieldsAndSections, payload];
            return set('openFieldsAndSections', openFieldsAndSections, state);
        }
        case ClientDatasetActionTypes.REMOVE_ALL_CLIENT_DATASETS_FIELD_SECTIONS:
            return set('openFieldsAndSections', INITIAL_STATE.openFieldsAndSections, state);
        case ClientDatasetActionTypes.TOGGLE_MODAL_DATASET_ID:
            return set('modalDatasetId', payload, state);
        case ClientDatasetActionTypes.TOGGLE_HIDDEN_FIELDS: {
            const { datasetId, hiddenField } = payload;
            const currentHiddenFields = state.currentHiddenFields[datasetId] || [];
            let updatedHiddenFields = currentHiddenFields;
            const shouldRemoveField = currentHiddenFields.find(field => isEqual(field, hiddenField));
            updatedHiddenFields = shouldRemoveField ? updatedHiddenFields.filter(field => !isEqual(field, hiddenField)) : [...updatedHiddenFields, hiddenField];
            updatedHiddenFields = sortHiddenFields(updatedHiddenFields);
            return set(`currentHiddenFields[${datasetId}]`, updatedHiddenFields, state);
        }
        case ClientDatasetActionTypes.SAVE_HIDDEN_FIELDS_STARTED:
            return set('isSaving', true, state);
        case ClientDatasetActionTypes.SAVE_HIDDEN_FIELDS_SUCCESSFUL:
            return flow(
                set('isSaving', false),
                set('savedHiddenFields', state.currentHiddenFields),
                set('hiddenFieldsUpdated', false),
                set('riskToleranceIsRecalculating', payload)
            )(state);
        case ClientDatasetActionTypes.SAVE_HIDDEN_FIELDS_FAILED:
            return set('isSaving', false, state);
        case ClientDatasetActionTypes.HIDDEN_FIELDS_HAVE_UPDATED:
            return set('hiddenFieldsUpdated', payload, state);
        case ClientDatasetActionTypes.TOGGLE_UNSAVED_CHANGES_MODAL:
            return set('unsavedChangesModalOpen', payload, state);
        case ClientDatasetActionTypes.RESET_CLIENT_DATASETS:
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
